import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
    fetchUnresolvedActiveConversations,
    resolveActiveConversationsMessage,
    summariseConversations,
    claimConversationOwnership,
    fetchConversationNotes,
    addConversationNote,
    removeConversationNote,
} from './activeConversationsAPI';
import { IBrokerConnectionActiveConversationsData } from '../../../model/brokerConnections/IBrokerConnectionActiveConversationsData';
import { ConversationsSummaryResponse } from '../../../model/summaries/ConversationsSummaryResponse';
import { ConversationNotesResponse } from '../../../model/brokerConnections/ConversationNotesResponse';
import { IPagination, LoadStatus } from '../../../model';
import { createAppThunk } from '../../thunks';
import { getShiftTimeRange, ShiftPeriod } from '../../../utils/shiftTimes';

export interface ActiveConversationsState {
    items: IBrokerConnectionActiveConversationsData[];
    loadStatus: LoadStatus;
    error: string | null;
    selectedIds: string[];
    teamIdFilter: null | string;
    unreadOnly: boolean;
    searchFlightQuery: string; // flight number search filter
    statusFilter: string; // conversation status filter (exact match)
    initiatedFromFilter: string; // initiated from filter (exact match)
    awaitingReplyOnly: boolean;
    arrivingSoonOnly: boolean;
    resolvingIds: string[]; // IDs currently being resolved
    markingAsResolvedId: string | null; // ID of conversation being marked as resolved (modal path)
    summary: {
        data: ConversationsSummaryResponse | null;
        loading: boolean;
        error: string | null;
        selectedShiftPeriod: ShiftPeriod | null;
    };
    conversationNotes: {
        data: ConversationNotesResponse[];
        loading: boolean;
        error: string | null;
    };
    pagination: IPagination;
}

const initialState: ActiveConversationsState = {
    items: [],
    loadStatus: 'idle',
    error: null,
    selectedIds: [],
    teamIdFilter: null,
    unreadOnly: false,
    searchFlightQuery: '',
    statusFilter: '',
    initiatedFromFilter: '',
    awaitingReplyOnly: false,
    arrivingSoonOnly: false,
    resolvingIds: [],
    markingAsResolvedId: null,
    summary: {
        data: null,
        loading: false,
        error: null,
        selectedShiftPeriod: null,
    },
    conversationNotes: {
        data: [],
        loading: false,
        error: null
    },
    pagination: {
        currentPage: 1,
        pageSize: 0, // Disabled
        totalCount: 0,
    },
};

// Load unresolved conversations
export const loadActiveConversations = createAppThunk<
    IBrokerConnectionActiveConversationsData[],
    void
>('activeConversations/unresolvedConversations', async (_, { getState }) => {
    const {
        activeConversations: { teamIdFilter },
    } = getState();
    return await fetchUnresolvedActiveConversations(
        teamIdFilter ?? undefined
    );
});

// Mark selected conversations resolved then refresh list
export const markConversationsResolved = createAppThunk<string[], string[]>(
    'activeConversations/markConversationsResolved',
    async (ids, { dispatch }) => {
        const resolvedIds: string[] = [];
        for (const id of ids) {
            try {
                await resolveActiveConversationsMessage(id);
                resolvedIds.push(id);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to resolve conversation', id, e);
            }
        }
        // Force a fresh pull of remaining unresolved conversations
        await dispatch(loadActiveConversations());
        return resolvedIds;
    }
);

// Fetch summary of conversations from specified time period
export const fetchSummary = createAppThunk<ConversationsSummaryResponse, { shift: ShiftPeriod }>(
    'activeConversations/fetchSummary',
    async ({ shift }, { getState }) => {
        const {
            activeConversations: { teamIdFilter },
        } = getState();
        const { startTime, endTime } = getShiftTimeRange(shift);

        return await summariseConversations(teamIdFilter ?? '', startTime, endTime);
    }
);

// Claim ownership of a conversation
export const claimConversation = createAppThunk<
    void,
    string
>('activeConversations/claimConversation', async (brokerConnectionId, { dispatch }) => {
    await claimConversationOwnership(brokerConnectionId);
    await dispatch(loadActiveConversations());
});

// Fetch notes for a conversation
export const loadConversationNotes = createAppThunk<
    ConversationNotesResponse[],
    string
>('activeConversations/fetchConversationNotes', async (conversationId) => {
    return await fetchConversationNotes(conversationId);
});

// Add conversation note
export const addConversationNoteThunk = createAppThunk<
    ConversationNotesResponse[],
    { conversationId: string; note: string }
>('activeConversations/addConversationNote', async ({ conversationId, note }, { dispatch }) => {
    await addConversationNote(conversationId, { note });
    return await dispatch(loadConversationNotes(conversationId)).unwrap();
});

// Remove conversation note
export const removeConversationNoteThunk = createAppThunk<
    ConversationNotesResponse[],
    { conversationId: string; noteId: string }
>('activeConversations/removeConversationNote', async ({ conversationId, noteId }, { dispatch }) => {
    await removeConversationNote(conversationId, noteId);
    return await dispatch(loadConversationNotes(conversationId)).unwrap();
});

export const activeConversationsSlice = createSlice({
    name: 'activeConversations',
    initialState,
    reducers: {
        toggleSelect: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            if (state.selectedIds.length === 1 && state.selectedIds[0] === id) {
                state.selectedIds = [];
            } else {
                state.selectedIds = [id];
            }
        },
        setSelected: (state, action: PayloadAction<string[]>) => {
            state.selectedIds = action.payload.slice(0, 1);
        },
        clearSelection: (state) => {
            state.selectedIds = [];
        },
        setTeamFilter: (state, action: PayloadAction<string | null>) => {
            state.teamIdFilter = action.payload;
        },
        setUnreadOnly: (state, action: PayloadAction<boolean>) => {
            state.unreadOnly = action.payload;
        },
        setSearchFlightQuery: (state, action: PayloadAction<string>) => {
            state.searchFlightQuery = action.payload;
        },
        setStatusFilter: (state, action: PayloadAction<string>) => {
            state.statusFilter = action.payload;
        },
        setInitiatedFromFilter: (state, action: PayloadAction<string>) => {
            state.initiatedFromFilter = action.payload;
        },
        setAwaitingReplyOnly: (state, action: PayloadAction<boolean>) => {
            state.awaitingReplyOnly = action.payload;
        },
        setArrivingSoonOnly: (state, action: PayloadAction<boolean>) => {
            state.arrivingSoonOnly = action.payload
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage =
                action.payload < 1 ? 1 : action.payload;
        },
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pagination.pageSize = action.payload < 0 ? 0 : action.payload;
            state.pagination.currentPage = 1; // reset on size change
        },
        updateConversationInList: (
            state,
            action: PayloadAction<{ conversationId: string; isRead: boolean }>
        ) => {
            const index = state.items.findIndex(
                (item) => item.id === action.payload.conversationId
            );
            if (index !== -1) {
                state.items[index] = {
                    ...state.items[index],
                    isRead: action.payload.isRead.toString(),
                };
            }
        },
        removeConversationFromList: (state, action: PayloadAction<string>) => {
            state.items = state.items.filter(
                (item) => item.id !== action.payload
            );
            state.pagination.totalCount = state.items.length;
        },
        setMarkingAsResolvedId: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.markingAsResolvedId = action.payload;
        },
        clearSummary: (state) => {
            state.summary.data = null;
            state.summary.error = null;
            state.summary.selectedShiftPeriod = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadActiveConversations.pending, (state) => {
                state.loadStatus = 'loading';
                state.error = null;
            })
            .addCase(loadActiveConversations.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = action.payload;
                state.pagination.totalCount = action.payload.length;
            })
            .addCase(loadActiveConversations.rejected, (state, action) => {
                state.loadStatus = 'failed';
                state.error = action.error.message ?? null;
            })
            .addCase(markConversationsResolved.pending, (state, action) => {
                const idsToResolve = action.meta.arg;
                state.resolvingIds = idsToResolve;
                state.selectedIds = [];
                state.markingAsResolvedId = idsToResolve[0] ?? null;
            })
            .addCase(markConversationsResolved.fulfilled, (state, action) => {
                state.resolvingIds = [];
                state.markingAsResolvedId = null;
                const resolvedIds = action.payload;
                state.items = state.items.filter(
                    (item) => !resolvedIds.includes(item.id)
                );
                state.pagination.totalCount = state.items.length;
            })
            .addCase(markConversationsResolved.rejected, (state, action) => {
                state.loadStatus = 'failed';
                state.error = action.error.message ?? null;
                state.resolvingIds = [];
                state.markingAsResolvedId = null;
            })
            .addCase(fetchSummary.pending, (state, action) => {
                state.summary.loading = true;
                state.summary.error = null;
                state.summary.data = null;
                state.summary.selectedShiftPeriod = action.meta.arg.shift;
            })
            .addCase(fetchSummary.fulfilled, (state, action) => {
                state.summary.loading = false;
                state.summary.data = action.payload;
            })
            .addCase(fetchSummary.rejected, (state, action) => {
                state.summary.loading = false;
                state.summary.error =
                    action.error.message ?? 'Failed to fetch summary';
                state.summary.selectedShiftPeriod = null;
            })
            .addCase(loadConversationNotes.pending, (state) => {
                state.conversationNotes.loading = true;
                state.conversationNotes.error = null;
                state.conversationNotes.data = [];
            })
            .addCase(loadConversationNotes.fulfilled, (state, action) => {
                state.conversationNotes.loading = false;
                state.conversationNotes.data = action.payload;
            })
            .addCase(loadConversationNotes.rejected, (state, action) => {
                state.conversationNotes.loading = false;
                state.conversationNotes.error =
                    action.error.message ?? 'Failed to load conversation notes';
            })
            .addCase(addConversationNoteThunk.pending, (state) => {
                state.conversationNotes.loading = true;
                state.conversationNotes.error = null;
            })
            .addCase(addConversationNoteThunk.fulfilled, (state, action) => {
                state.conversationNotes.loading = false;
                state.conversationNotes.data = action.payload;
            })
            .addCase(addConversationNoteThunk.rejected, (state, action) => {
                state.conversationNotes.loading = false;
                state.conversationNotes.error =
                    action.error.message ?? 'Failed to add conversation note';
            })
            .addCase(removeConversationNoteThunk.pending, (state) => {
                state.conversationNotes.loading = true;
                state.conversationNotes.error = null;
            })
            .addCase(removeConversationNoteThunk.fulfilled, (state, action) => {
                state.conversationNotes.loading = false;
                state.conversationNotes.data = action.payload;
            })
            .addCase(removeConversationNoteThunk.rejected, (state, action) => {
                state.conversationNotes.loading = false;
                state.conversationNotes.error =
                    action.error.message ?? 'Failed to delete conversation note';
            });
    },
});

export const selectActiveConversations = (state: RootState) =>
    state.activeConversations;
export const selectActiveConversationsSelectedIds = (state: RootState) =>
    state.activeConversations.selectedIds;
export const selectActiveConversationsPagination = (state: RootState) =>
    state.activeConversations.pagination;
export const selectResolvingIds = (state: RootState) =>
    state.activeConversations.resolvingIds;
export const selectMarkingAsResolvedId = (state: RootState) =>
    state.activeConversations.markingAsResolvedId;
export const selectSummary = (state: RootState) =>
    state.activeConversations.summary;
export const selectConversationNotes = (state: RootState) =>
    state.activeConversations.conversationNotes;

export const {
    toggleSelect,
    setSelected,
    clearSelection,
    setTeamFilter,
    setUnreadOnly,
    setArrivingSoonOnly,
    setSearchFlightQuery,
    setStatusFilter,
    setInitiatedFromFilter,
    setCurrentPage,
    setPageSize,
    setAwaitingReplyOnly,
    updateConversationInList,
    removeConversationFromList,
    setMarkingAsResolvedId,
    clearSummary,
} = activeConversationsSlice.actions;

export default activeConversationsSlice.reducer;
